import { parseMaybeProductDTO, ProductDTO } from './product-dto.js'
import { createValidate } from 'typia'
import { validateGuard } from 'components/shared/parsing/parsing.js'
import type { StepType } from './step-type.js'

// --- Attribute Detail Option ---

export type IAttributeDetailOptionDTO = {
  identifier: number
  attribute: number
  name: string
  code: string | null
  required: boolean
  values: string[]
  type: 'selection' | 'text'
  selected: unknown | null
}

export class AttributeDetailOptionDTO {
  identifier: number
  attribute: number
  name: string
  code: string | null
  required: boolean
  values: string[]
  type: 'selection' | 'text'
  selected: unknown | null

  constructor(data: IAttributeDetailOptionDTO) {
    this.identifier = data.identifier
    this.attribute = data.attribute
    this.name = data.name
    this.code = data.code
    this.required = data.required
    this.values = data.values
    this.type = data.type
    this.selected = data.selected
  }
}

const validateAttributeDetailOptionDTO = createValidate<IAttributeDetailOptionDTO>()

export const parseAttributeDetailOptionDTO = (data: unknown): AttributeDetailOptionDTO => {
  validateGuard(validateAttributeDetailOptionDTO, data, 'AttributeDetailOptionDTO')
  return new AttributeDetailOptionDTO(data)
}

// --- Attribute Option ---

export type IAttributeOptionDTO = {
  id: number
  name: string
  code: string
  data?: unknown
  selectable: boolean
  selected: boolean
}

export class AttributeOptionDTO {
  id: number
  name: string
  code: string
  data: ProductDTO | undefined
  selectable: boolean
  selected: boolean

  constructor(data: IAttributeOptionDTO) {
    this.id = data.id
    this.name = data.name
    this.code = data.code
    this.data = parseMaybeProductDTO(data.data)
    this.selectable = data.selectable
    this.selected = data.selected
  }
}

const validateAttributeOptionDTO = createValidate<IAttributeOptionDTO>()

export const parseAttributeOptionDTO = (data: unknown): AttributeOptionDTO => {
  validateGuard(validateAttributeOptionDTO, data, 'AttributeOptionDTO')
  return new AttributeOptionDTO(data)
}

// --- Product Option ---

export type IProductOptionDTO = {
  id: number
  name: string
  code: string
  data?: unknown
  selectable: boolean
  selected: boolean
}

export class ProductOptionDTO {
  id: number
  name: string
  code: string
  data: ProductDTO | undefined
  selectable: boolean
  selected: boolean

  constructor(data: IProductOptionDTO) {
    this.id = data.id
    this.name = data.name
    this.code = data.code
    this.data = parseMaybeProductDTO(data.data)
    this.selectable = data.selectable
    this.selected = data.selected
  }
}

const validateProductOptionDTO = createValidate<IProductOptionDTO>()

export const parseProductOptionDTO = (data: unknown): ProductOptionDTO => {
  validateGuard(validateProductOptionDTO, data, 'ProductOptionDTO')
  return new ProductOptionDTO(data)
}

// --- Polymorphic Options parser ---

type OptionDTO = AttributeOptionDTO | ProductOptionDTO | AttributeDetailOptionDTO

export const parseOptionsDTO = (data: unknown, stepType: StepType): OptionDTO => {
  if (stepType === 'attribute_set') return parseAttributeDetailOptionDTO(data)
  if (stepType === 'product_set') return parseProductOptionDTO(data)
  if (stepType === 'attribute') return parseAttributeOptionDTO(data)
  throw new Error(`Unknown OptionDTO type: "${stepType}", ${data}`)
}
